import { memo, useMemo } from "react";
import { BaseModal } from "@/react/components/editors/base/BaseModal";
import {
  getWidgetConfig,
  type EntityTypeKey,
  type WidgetId,
} from "@/other/constants/EntityManagerConfig";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys";
import type { EntityRecord } from "@/types/entity";
import type { GeneralWidgetOtherParams } from "./widgetOtherParams";

export type GeneralWidgetModalRecord = {
  widgetKey: WidgetId;
  entityRecord: EntityRecord;
  entityType: EntityTypeKey;
  defaultPosition?: { x: number; y: number };
  headerTitle?: string;
  otherParams?: GeneralWidgetOtherParams;
};

interface GeneralWidgetModalProps {
  record?: GeneralWidgetModalRecord | null;
  onClose: () => void;
  editorInstance?: {
    className?: string;
    [key: string]: unknown;
  };
}

const MODAL_SIZE = {
  width: "min(33rem, 100vw)",
  height: "min(35rem, 100vh)",
  minHeight: "35rem",
  minWidth: "33rem",
  maxHeight: "90vh",
  maxWidth: "90vw",
};

const renderNullHeader = () => null;

const isEntityTypeKey = (type: string): type is EntityTypeKey =>
  Object.values(ENTITY_TYPES).includes(type as EntityTypeKey);

type GeneralWidgetModalContentProps = {
  widgetKey: WidgetId;
  entityRecord: EntityRecord;
  entityType: EntityTypeKey;
  headerTitle?: string;
  otherParams?: GeneralWidgetOtherParams;
  onClose: () => void;
};

const GeneralWidgetModalContent = memo(function GeneralWidgetModalContent({
  widgetKey,
  entityRecord,
  entityType,
  headerTitle,
  otherParams,
  onClose,
}: GeneralWidgetModalContentProps) {
  const widget = getWidgetConfig(widgetKey);
  const WidgetComponent = widget?.component;
  if (!WidgetComponent) return null;

  // Media / Documents components wrap themselves in EntityFileProvider — do not
  // double-wrap here or /file/list fires twice (unfiltered + filtered).
  return (
    <div className="general-widget-drag-handle h-full min-h-0 overflow-auto">
      <WidgetComponent
        record={entityRecord}
        entityType={entityType}
        onClose={onClose}
        headerTitle={headerTitle}
        {...(otherParams ?? {})}
      />
    </div>
  );
});

export const GeneralWidgetModal = ({ record, onClose, editorInstance }: GeneralWidgetModalProps) => {
  const { widgetKey, entityRecord, entityType, headerTitle, otherParams } = record ?? {};

  const validationError = useMemo(() => {
    if (!widgetKey || !entityRecord || !entityType) {
      return "Missing widgetKey, entityRecord, or entityType.";
    }
    if (!isEntityTypeKey(entityType)) {
      return `Unknown entity type: ${entityType}`;
    }
    if (!getWidgetConfig(widgetKey)) {
      return `Unknown widget: ${widgetKey}`;
    }
    return null;
  }, [widgetKey, entityRecord, entityType]);

  const modalContent = useMemo(() => {
    if (validationError) {
      return <p className="text-sm text-muted-foreground">{validationError}</p>;
    }
    if (!widgetKey || !entityRecord || !entityType || !isEntityTypeKey(entityType)) {
      return null;
    }
    return (
      <GeneralWidgetModalContent
        widgetKey={widgetKey}
        entityRecord={entityRecord}
        entityType={entityType}
        headerTitle={headerTitle}
        otherParams={otherParams}
        onClose={onClose}
      />
    );
  }, [validationError, widgetKey, entityRecord, entityType, headerTitle, otherParams, onClose]);

  return (
    <BaseModal
      record={record ?? {}}
      onClose={onClose}
      title=""
      description=""
      editorInstance={editorInstance}
      hideMenu
      size={MODAL_SIZE}
      modal={false}
      version={undefined}
      swivuAssistanceConfig={undefined}
      headerIconCls=""
      headerRenderer={renderNullHeader}
      dragHandleSelector=".general-widget-drag-handle"
      className="bg-background border-0 overflow-hidden"
      defaultPosition={record?.defaultPosition}
    >
      {modalContent}
    </BaseModal>
  );
};
